import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Download, Eye, Edit, Trash2, Copy, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import { useMediosPago, useMedioPagoMutations } from '../../../graphql/tesoreria';

export default function ListadoMediosPago() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ tipo: '', estado: '' });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const [result, refetch] = useMediosPago({ search: searchTerm, ...filters, page, pageSize });
  const { deleteMedio } = useMedioPagoMutations();

  const medios = result.data?.mediosPago?.data || [];
  const pagination = result.data?.mediosPago?.pagination || { total: 0, page: 1, pageSize: 10, totalPages: 1 };

  const handleDelete = async (medio) => {
    if (window.confirm(`¿Eliminar medio "${medio.nombre}"?`)) {
      try {
        await deleteMedio({ id: medio.id });
        alert('Medio eliminado correctamente');
        refetch();
      } catch (error) {
        alert('Error al eliminar el medio');
      }
    }
  };

  const exportToCSV = () => {
    const headers = ['Nombre', 'Tipo', 'Cuenta Bancaria', 'Habilitado'];
    const rows = medios.map((m) => [
      m.nombre,
      m.tipo,
      m.cuentaBancaria ? `${m.cuentaBancaria.banco} - ${m.cuentaBancaria.descripcion}` : 'N/A',
      m.habilitado ? 'Sí' : 'No',
    ]);
    const csvContent = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `medios_pago_${new Date().toISOString()}.csv`;
    link.click();
  };

  const getTipoBadgeColor = (tipo) => {
    const colors = {
      EFECTIVO: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      TRANSFERENCIA: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      TARJETA: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      MONI: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    };
    return colors[tipo] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))]">
      <PageHeader
        title="Medios de Pago"
        subtitle="Gestión de medios de pago y cobro"
        actions={[
          <button
            key="nuevo"
            onClick={() => navigate('/Tesoreria/MediosPago/Nuevo')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            Nuevo Medio
          </button>,
        ]}
      />

      <section className="max-w-[1400px] mx-auto px-4 py-6 grid grid-cols-12 gap-4">
        <div className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] overflow-hidden">
          <div className="p-4 border-b border-[rgb(var(--border))] bg-[rgba(var(--subpanel))]">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[rgb(var(--muted))]" aria-hidden="true" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar medios de pago..."
                  className="w-full pl-10 pr-4 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={filters.tipo}
                  onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
                  className="px-3 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                >
                  <option value="">Todos los tipos</option>
                  <option value="EFECTIVO">Efectivo</option>
                  <option value="TRANSFERENCIA">Transferencia</option>
                  <option value="TARJETA">Tarjeta</option>
                  <option value="MONI">Moni</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
                <button onClick={exportToCSV} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]">
                  <Download className="w-4 h-4" aria-hidden="true" />
                  Exportar
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[rgba(var(--subpanel))] border-b border-[rgb(var(--border))]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Cuenta Asociada</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--border))]">
                {medios.map((medio) => (
                  <tr key={medio.id} className="hover:bg-[rgba(var(--muted),0.2)] transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-[rgb(var(--fg))]">{medio.nombre}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTipoBadgeColor(medio.tipo)}`}>
                        {medio.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[rgb(var(--muted))]">
                      {medio.cuentaBancaria ? `${medio.cuentaBancaria.banco} - ${medio.cuentaBancaria.descripcion}` : 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${medio.habilitado ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'}`}>
                        {medio.habilitado ? 'Habilitado' : 'Deshabilitado'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => navigate(`/Tesoreria/MediosPago/${medio.id}/Editar`)} className="p-1.5 rounded-lg hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-[rgb(var(--brand))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]" title="Editar">
                          <Edit className="w-4 h-4" aria-hidden="true" />
                        </button>
                        <button onClick={() => handleDelete(medio)} className="p-1.5 rounded-lg hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]" title="Eliminar">
                          <Trash2 className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
